import { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import { ICaseInfo, ICaseStage, Stage_Status } from 'src/shared/types/Case';
import { useFormatDate } from 'src/utils/hooks/useFormatDate';
import StageNotes, { INote } from 'src/components/StageNotes/StageNotes';
import styles from './CaseStage.module.scss';
import ProfileImage from 'src/components/ProfileImage/ProfileImage';
import { ButtonSize } from '@ds/elements/Button/Button.types';
import { Button, Loader } from '@ds';
import { AidiProgressStatus } from 'src/utils/hooks/useAidiAccess';
import { useAidiAccessImmediate } from 'src/utils/hooks/useAidiAccessImmediate';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import CheckmarkCircle from '@icons/checkmark-circle.svg';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CaseDocumentsTabs } from 'src/utils/constants/Case';
import { AIDI_SUPPORTED_QUESTIONNAIRE_NAMES, GATHERING_DOCS_STAGE_NAME } from 'src/utils/constants/common';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { DateFormat, getFormattedDate } from 'src/utils/formatters/formatDate';
import { getAidiCasePath, getQStatusFromQuestionnaire } from 'src/utils/services/aidiQuestionnaire';

enum StatusClass {
  COMPLETED = 'completed',
  STARTED = 'preparing',
  NOT_STARTED = 'notstarted',
}

enum StageStatus {
  NOT_STARTED = 'Not started',
  IN_PROGRESS = 'Stage in progress.',
  COMPLETED = 'Stage completed.',
}

const StageIcons: Record<string, string> = {
  'Case Initiated': 'case-initiated',
  'Case Initiation & Information Gathering': 'case-initiated',
  'Gathering Docs & Information': 'gathering-info-documents',
  'Reviewing Information & Preparing Application': 'gathering-info-documents',
  'Summarizing Position Requirements': 'summarizing-requirements',
  'Filing with Government': 'filing-with-government',
  'Decision from Government': 'decision-from-government',
  'Securing Experience Verification Letters and Job Classification': 'securing-experience-verification-letters',
  'Recruitment in Progress': 'recruitment',
  'Preparing & Drafting': 'preparing-and-drafting',
  'Preparing & Drafting PERM Form': 'preparing-and-drafting',
  'Filing your Application & Decision': 'application-decision',
  'Government Processing & Decision': 'application-decision',
  'Case Completion': 'case-completion',
};

const getStageIcon = (stageName: string) => {
  const lowerCaseStageName = stageName.toLowerCase();
  for (const key in StageIcons) {
    if (key.toLowerCase() === lowerCaseStageName) {
      return StageIcons[key as keyof typeof StageIcons];
    }
  }
  return '';
};

const getDefaultNotes = (status: Stage_Status): INote => {
  return {
    detail:
      status === Stage_Status.STARTED
        ? StageStatus.IN_PROGRESS
        : status === Stage_Status.NOT_STARTED
          ? StageStatus.NOT_STARTED
          : StageStatus.COMPLETED,
    date: '',
    id: 0,
  };
};

const CaseStage = (props: {
  stage: ICaseStage;
  isUSCountry?: boolean;
  isRemote?: boolean;
  caseDetails?: ICaseInfo;
}) => {
  const { stage, isUSCountry = false, isRemote = false, caseDetails } = props;
  const { status, name, completionDate, notes: stageNotes } = stage;
  const formattedCompletionDate = useFormatDate(completionDate);
  const isStarted = status === Stage_Status.STARTED;
  const isNotStarted = status === Stage_Status.NOT_STARTED;
  const isInProgress = isStarted || (isNotStarted && stageNotes.length > 0);
  const [isNoteExpanded, setIsNoteExpanded] = useState(stageNotes.length > 0 && isInProgress);
  const notes: INote[] = stageNotes.length > 0 ? stageNotes : [getDefaultNotes(status)];
  const getLatestNoteDetail = () =>
    [...notes].sort((a, b) => (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0))[0]
      ?.detail ?? '';
  const stageIcon = getStageIcon(name);
  const statusClass = isInProgress ? StatusClass.STARTED : StatusClass[status];

  const {
    shouldShowAidiEntry,
    aidiProgressStatus,
    loading: isLoadingAidi,
    matchedQuestionnaires,
    employeeQuestionnaires,
  } = useAidiAccessImmediate({
    caseId: caseDetails?.caseId || null,
    aidiStatus: true,
  });
  const { data: userInfo } = useGetUser();
  const userDateFormat = (userInfo?.userSettings?.dateFormat as DateFormat) ?? DateFormat.DEFAULT;

  const formattedDate = useFormatDate(stage.startDate);
  const isGatheringDocsStage = name === GATHERING_DOCS_STAGE_NAME;
  const isIdleProgressStatus = aidiProgressStatus === AidiProgressStatus.IDLE;
  const isCompletedProgressStatus = aidiProgressStatus === AidiProgressStatus.COMPLETED;

  // AIDI-supported questionnaires only get an AIDI card once AIDI access is actually granted for
  // the case (flag + stage), and each one gets its own card since they each navigate to a
  // different AIDI questionnaire. Any other assigned questionnaire (dynamic/legacy-only, e.g. FBI
  // Clearance) is only used to decide whether to show a single shared Legacy card (below) - it
  // always routes to the same case-documents Questionnaire tab, so there's no need for one card
  // per legacy questionnaire.
  const aidiTaskQuestionnaires = shouldShowAidiEntry ? matchedQuestionnaires : [];
  const legacyTaskQuestionnaires = employeeQuestionnaires.filter(
    (q) => !AIDI_SUPPORTED_QUESTIONNAIRE_NAMES.includes(q.questionnaire)
  );
  const hasAnyQuestionnaireTasks = aidiTaskQuestionnaires.length > 0 || legacyTaskQuestionnaires.length > 0;

  const shouldShowTaskSection =
    isGatheringDocsStage && hasAnyQuestionnaireTasks && (isInProgress || status === Stage_Status.COMPLETED);
  // Generic fallback card for when no specific questionnaire (AIDI or Legacy) is assigned to the
  // case yet, but the case is still in Gathering Docs (e.g. general document upload is needed).
  const shouldShowNonAidiTaskSection = isGatheringDocsStage && !isLoadingAidi && !hasAnyQuestionnaireTasks && isInProgress;
  const UPLOAD_DOCUMENTS_TASK_MESSAGE = 'Upload documents and information needed to process your case.';
  const { navigate } = useNavigateWithSecureParams();
  const flags = useFlags();

  // Only used by the generic Legacy fallback card (shouldShowNonAidiTaskSection, which by
  // definition only renders when there is no assigned questionnaire at all). aidiProgressStatus
  // comes from AIDI's own stage record, which can remain stale/stuck (e.g. still "in progress")
  // after an AIDI-eligible questionnaire is unassigned from the case. Since this card always routes
  // to Legacy case-documents regardless of that AIDI state, it must not borrow AIDI's progress
  // wording.
  const getButtonText = () => {
    if (!shouldShowAidiEntry) return 'PROCEED';
    if (isCompletedProgressStatus) return 'VIEW';
    if (isIdleProgressStatus) return 'START';
    return 'CONTINUE';
  };
  const buttonText = getButtonText();

  useEffect(() => {
    setIsNoteExpanded(shouldShowTaskSection || shouldShowNonAidiTaskSection);
  }, [shouldShowTaskSection, shouldShowNonAidiTaskSection]);

  const useUrlCaseId = useMemo(() => flags['fnx-show-backtodocuments'] ?? false, [flags]);

  const getQAidiPath = (qStatus: AidiProgressStatus): string => getAidiCasePath(caseDetails?.caseId, qStatus, useUrlCaseId);

  const TaskNoteCompleted = ({
    completedOn,
    buttonLabel,
    onProceed,
  }: {
    completedOn: string;
    buttonLabel: string;
    onProceed: () => void;
  }) => {
    return (
      <div className={`flex flex-col gap-2 ${classNames(styles['stage-task-completed-container'])}`}>
        <div className={'flex flex-row items-center justify-between'}>
          <div className="flex flex-row items-center gap-2">
            <div className={styles['red-dot-icon']}></div>
            <div className="flex flex-col">
              <span className={styles['stage-task-title']}>Your Task</span>
            </div>
          </div>
          {formattedDate && <p className={styles['stage-task-start-date']}>{formattedDate}</p>}
        </div>
        <div className={'flex flex-row items-start w-full'}>
          <ProfileImage />
          <p className={`flex-1 self-start ${styles['stage-task-msg']}`}>{UPLOAD_DOCUMENTS_TASK_MESSAGE}</p>
          <Button
            className={styles['submit-button']}
            size={ButtonSize.medium}
            onClick={() => {
              if (isLoadingAidi) return;
              onProceed();
            }}
          >
            {isLoadingAidi ? <Loader size={18} /> : buttonLabel}
          </Button>
        </div>
        {completedOn && (
          <div className={'flex flex-row items-start w-full'}>
            <p className={styles['stage-task-completed-date']}>
              <img src={CheckmarkCircle} className={styles['checkmark-icon']} />
              Completed: {completedOn}
            </p>
          </div>
        )}
      </div>
    );
  };

  const TaskNote = ({ onProceed, buttonLabel }: { onProceed: () => void; buttonLabel: string }) => {
    return (
      <div className={`flex flex-col gap-2 ${classNames(styles['stage-task-container'])}`}>
        <div className={'flex flex-row items-center justify-between'}>
          <div className="flex flex-row items-center gap-2">
            <div className={styles['red-dot-icon']}></div>
            <div className="flex flex-col">
              <span className={styles['stage-task-title']}>Your Task</span>
            </div>
          </div>
          {formattedDate && <p className={styles['stage-task-start-date']}>{formattedDate}</p>}
        </div>
        <div className={'flex flex-row items-start w-full'}>
          <ProfileImage />
          <p className={`flex-1 self-start ${styles['stage-task-msg']}`}>{UPLOAD_DOCUMENTS_TASK_MESSAGE}</p>
          <Button
            className={styles['submit-button']}
            size={ButtonSize.medium}
            onClick={() => {
              if (isLoadingAidi) return;
              onProceed();
            }}
          >
            {isLoadingAidi ? <Loader size={18} /> : buttonLabel}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames(styles['stage-details-container'], {
        [styles['stage-details-global']]: !isUSCountry,
        [styles['remote-stage-details-container']]: isRemote,
      })}
    >
      <div className={styles['stage-name-container']}>
        <div className={styles['stage-name-details']}>
          <div
            className={classNames(styles['stage-icon'], {
              [styles['stage-icon-active']]: isInProgress,
              [styles[`${stageIcon}-active`]]: isInProgress,
              [styles[`${stageIcon}`]]: !isInProgress,
            })}
          ></div>
          <p className={styles[`${statusClass}`]}>{name}</p>
        </div>
        {isInProgress && (
          <div className={styles['stage-in-progress']}>
            <div className={styles['stage-in-progress-icon']}></div>
            <p>In Progress</p>
          </div>
        )}
      </div>

      {!isRemote && (
        <>
          <div
            className={classNames(styles['stage-progress-indicator-line'], {
              [styles[`${statusClass}`]]: true,
            })}
          ></div>
          <div className={styles['stage-more-details']}>
            <div className={styles['stage-notes-wrapper']}>
              {isNoteExpanded ? (
                <div className={styles['stage-notes-expanded']}>
                  {
                    <>
                      {shouldShowTaskSection ? (
                        <>
                          {aidiTaskQuestionnaires.map((questionnaire) => {
                            const qStatus = getQStatusFromQuestionnaire(questionnaire);
                            // qStatus reflects the questionnaire form state (started/completed in AIDI).
                            // aidiProgressStatus reflects whether AIDI has any record of this case
                            // (derived from the AIDI stage endpoint: 404 -> IDLE, otherwise IN_PROGRESS
                            // or COMPLETED). These are separate signals: a user can upload docs without
                            // ever opening the AIDI form, leaving qStatus as IDLE even though AIDI
                            // already knows about the case.
                            //
                            // effectiveQStatus merges both: if the form looks untouched (IDLE) but
                            // AIDI reports progress (IN_PROGRESS), treat the effective state as
                            // IN_PROGRESS. This keeps the button label ("CONTINUE") and the navigation
                            // path (case page, not welcome page) consistent with each other.
                            const effectiveQStatus =
                              qStatus === AidiProgressStatus.IDLE && aidiProgressStatus === AidiProgressStatus.IN_PROGRESS
                                ? AidiProgressStatus.IN_PROGRESS
                                : qStatus;
                            const isReopened = aidiProgressStatus === AidiProgressStatus.REOPENED;
                            const qButtonText = isReopened
                              ? 'EDIT FORM'
                              : effectiveQStatus === AidiProgressStatus.COMPLETED
                                ? 'VIEW'
                                : effectiveQStatus === AidiProgressStatus.IDLE
                                  ? 'START'
                                  : 'CONTINUE';
                            const qCompletedOn = questionnaire.submittedDateOnly
                              ? getFormattedDate(questionnaire.submittedDateOnly, userDateFormat)
                              : '';
                            const qPath = getQAidiPath(effectiveQStatus);
                            return effectiveQStatus === AidiProgressStatus.COMPLETED && !isReopened ? (
                              <TaskNoteCompleted
                                key={questionnaire.caseQuestionnaireId}
                                completedOn={qCompletedOn}
                                buttonLabel={qButtonText}
                                onProceed={() =>
                                  navigate(qPath, {
                                    secureParams: {
                                      caseId: caseDetails?.caseId,
                                      questionnaireId: questionnaire.caseQuestionnaireId,
                                    },
                                  })
                                }
                              />
                            ) : (
                              <TaskNote
                                key={questionnaire.caseQuestionnaireId}
                                buttonLabel={qButtonText}
                                onProceed={() =>
                                  navigate(qPath, {
                                    secureParams: {
                                      caseId: caseDetails?.caseId,
                                      questionnaireId: questionnaire.caseQuestionnaireId,
                                    },
                                  })
                                }
                              />
                            );
                          })}
                          {legacyTaskQuestionnaires.length > 0 && (
                            // Dynamic/legacy-only questionnaires (e.g. FBI Clearance Questionnaire)
                            // assigned alongside an AIDI-supported one all route to the same Legacy
                            // case-documents Questionnaire tab, where each one can be opened
                            // individually - so a single shared card covers any number of them,
                            // instead of one per questionnaire.
                            <TaskNote
                              key="legacy-questionnaires"
                              buttonLabel="PROCEED"
                              onProceed={() =>
                                navigate(`/case-documents`, {
                                  secureParams: {
                                    caseId: caseDetails?.caseId,
                                    tab: CaseDocumentsTabs.CaseDocuments,
                                  },
                                })
                              }
                            />
                          )}
                        </>
                      ) : shouldShowNonAidiTaskSection ? (
                        <TaskNote
                          buttonLabel={buttonText}
                          onProceed={() =>
                            navigate(`/case-documents`, {
                              secureParams: {
                                caseId: caseDetails?.caseId,
                                tab: CaseDocumentsTabs.CaseDocuments,
                              },
                            })
                          }
                        />
                      ) : null}
                      <StageNotes notes={notes} />
                    </>
                  }
                </div>
              ) : (
                notes[0] && (
                  <div className={styles['stage-notes-collapsed']}>
                    <p>
                      {status !== Stage_Status.COMPLETED && (shouldShowTaskSection || shouldShowNonAidiTaskSection)
                        ? UPLOAD_DOCUMENTS_TASK_MESSAGE
                        : getLatestNoteDetail()}
                    </p>
                  </div>
                )
              )}
              {(stage.notes?.length > 0 || shouldShowTaskSection || shouldShowNonAidiTaskSection) && (
                <div
                  className={classNames(
                    styles['stage-notes-toggle-icon'],
                    isNoteExpanded ? styles['stage-notes-expanded-icon'] : ''
                  )}
                  onClick={() => setIsNoteExpanded(!isNoteExpanded)}
                ></div>
              )}
            </div>
            {status === Stage_Status.COMPLETED && (
              <p className={styles['stage-completed-date']}>Completed: {formattedCompletionDate}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CaseStage;
